import classNames from "classnames";
import ConditionWrapper from "components/ConditionWrapper";
import { toastr } from "components/toastr";
import { JAVASCRIPT_VOID, VOD_PLAYER_DOMAIN, EVENT2_DOMAIN, APP_SCHEME } from "constant/config";
import { getPlaylistUrl, setClickLogapi, isWebview } from "helpers";
import React from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { isMobile } from "react-device-detect";
import { Trans, useTranslation } from "react-i18next";
import { NORMAL } from "constant/board";
import { kakaoMessage } from "libs/scriptLoad";

const SHARE_PAGE = {
    TWITTER: "twitter",
    FACEBOOK: "facebook",
    KAKAO: "kakao",
    TELEGRAM: "telegram",
    QQ: "qq",
    WEIBO: "weibo",
};

// 공유 페이지 종류로 클릭로그 link_type 조회
const getLinkType = (sharePage) => {
    switch (sharePage) {
        case SHARE_PAGE.TWITTER:
            return "twitter";

        case SHARE_PAGE.FACEBOOK:
            return "facebook";

        default:
            return "etc";
    }
};

const PlaylistShare = ({ playlist, close, top = 0, left = "unset", right = "unset" }) => {
    const { t } = useTranslation();

    let { bbsTitle: post = {}, rep_title_no : rep_title_no = 0 } = playlist;
    post = post || {};
    const { ucc = {}, station_no : stationNo = 0, bbs_no : bbsNo = 0, title_no : titleNo = 0 } = post;
    const { file_type: fileType = NORMAL } = ucc;

    // 게시글 공유 링크
    const shareUrl = getPlaylistUrl(playlist);

    // webchat qr code 이미지 링크
    const webChatQrCode = `${EVENT2_DOMAIN}/api/get_qrcode.php?szWork=share&type=vod&bjId=${post.user_id ||
        ""}&bbsNo=${post.bbs_no || 0}&vNo=${post.title_no || 0}`;

    // 외부 공유 링크
    const getExternalShareLink = (sharePage) => {
        const encodeShareUrl = encodeURIComponent(shareUrl),
            describe = encodeURIComponent(t("BJ {{nickname}}의 영상을 확인하세요!", { nickname: post.user_nick || "" })), // 게시글 설명
            summary = encodeURIComponent(t("SOOP 사이트(www.sooplive.co.kr)에서 더 많은 컨텐츠를 즐길 수 있습니다!")); // 사이트 요약

        // 게시글 제목
        let titleName = encodeURIComponent(playlist.title.replace(/(<([^>]+)>)/gi, "").replace(/"/g, "'"));

        // 공유시 노출할 이미지
        const shareImage = ucc.thumb;

        const lang = document.documentElement.lang.toLowerCase();

        switch (sharePage) {
            // 트위터 링크
            case SHARE_PAGE.TWITTER:
                return `https://twitter.com/intent/tweet?text=${titleName}&url=${encodeShareUrl}`;

            // 페이스북 공유 링크
            case SHARE_PAGE.FACEBOOK:
                return `https://www.facebook.com/sharer/sharer.php?u=${encodeShareUrl}`;

            // 중국 QQ 사이트 공유 링크
            case SHARE_PAGE.QQ:
                return `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodeShareUrl}&title=${titleName}&pics=${shareImage}&desc=${describe}&summary=${summary}(@小蜜蜂U小飞)`;

            // 중국 weibo 사이트 공유 링크
            case SHARE_PAGE.WEIBO:
                return `https://service.weibo.com/share/share.php?url=${encodeShareUrl}&title=${titleName}&pics=${shareImage}&desc=${describe}&language=${lang}`;

            // 카카오톡 공유 링크
            case SHARE_PAGE.KAKAO:

                // vod 템플릿 이용
                const templateId = 103712;
                const kakaoArgs = {
                    title : playlist.title.replace(/(<([^>]+)>)/gi, "").replace(/"/g, "'"),
                    imageUrl : "https:" + shareImage,   // 카카오톡 논스킴 적용 안되서 프로토콜 추가
                    videoNumber : shareUrl.split(VOD_PLAYER_DOMAIN + "/")[1],
                    scheme : encodeURIComponent(`${APP_SCHEME}player/video?station_no=${stationNo}&bbs_no=${bbsNo}&title_no=${rep_title_no}&nPlaylistIdx=${playlist.idx}&type=${fileType}`)
                }

                return { templateId, kakaoArgs };

            // 텔레그램 공유 링크
            case SHARE_PAGE.TELEGRAM:
                return `https://t.me/share/url?url=${encodeShareUrl}&text=${titleName}`;

            default:
                return "";
        }
    };

    // 외부 사이트 공유
    const handleShareExternal = (sharePage, windowName = "", windowFeatures = "") => () => {
        // 클릭 로그 수집
        sendVodPlaylistShareLog(getLinkType(sharePage));

        // 공유 사이트 열기
        if(sharePage === SHARE_PAGE.KAKAO) {
            const kakaoInfo = getExternalShareLink(sharePage);
            kakaoMessage(kakaoInfo, true);
        } else if(sharePage === SHARE_PAGE.TELEGRAM) {
            window.open((getExternalShareLink(sharePage)), "TelegramShare",
            "left=10,top=10,width=500,height=499,marginwidth=0,marginheight=0,resizable=0,scrollbars=no",);
        } else {
            window.open(getExternalShareLink(sharePage), windowName, windowFeatures);
        }
        close();
    };

    // URL 복사 공유
    const handleShareLink = () => {
        // 클릭 로그 수집
        sendVodPlaylistShareLog("url");

        toastr.success(t("주소가 복사되었습니다. 원하는 곳에 붙여넣기(Ctrl+V)해주세요"), {
            timeOut: 2000,
        });
        close();
    };

    // 클릭 로그 전송
    const sendVodPlaylistShareLog = (linkType) => {
        setClickLogapi("vod_playlist_share", {
            link_type: linkType,
            creator: playlist.reg_id,
            is_bulk: true,
        });
    };

    // 이벤트 취소
    const handleCancelEvent = (event) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
    };

    return (
        <div
            className={classNames(isMobile ? ["ui-pop-m", "bs-layer"] : ["ui-pop", "bs-sns_layer", "active"])}
            style={isMobile ? {} : { position: "absolute", top, left, right }}
            onClick={!isMobile && handleCancelEvent}
        >
            {/* 팝업 제목 */}
            <div className="pop-title">
                <ConditionWrapper condition={!isMobile} wrapper={"h3"}>
                    <Trans>공유하기</Trans>
                </ConditionWrapper>
            </div>

            <div className={classNames("pop-body", { "pop-sns": isMobile })}>
                <div className={classNames({ "flex-grid": isMobile && isWebview })}>
                    {/* 트위터 공유 */}
                    <button type="button" className="tw" onClick={handleShareExternal(SHARE_PAGE.TWITTER)}>
                        <em />
                    </button>

                    {/* 페이스북 공유 */}
                    <button type="button" className="fb" onClick={handleShareExternal(SHARE_PAGE.FACEBOOK)}>
                        <em />
                    </button>


                    {/* 카카오톡 공유 */}
                    <button type="button" className="kakao" onClick={handleShareExternal(SHARE_PAGE.KAKAO)} >
                        <em></em>
                    </button>

                    {/* 텔레그램 공유 */}
                    <button type="button" className="tg" onClick={handleShareExternal(SHARE_PAGE.TELEGRAM)}>
                        <em></em>
                    </button>

                    {/* 중국 QA 공유 */}
                    <button type="button" className="qq" onClick={handleShareExternal(SHARE_PAGE.QQ, "shareQ")}>
                        <em />
                    </button>

                    {/* 중국 WEIBO 공유 */}
                    <button type="button" className="weibo" onClick={handleShareExternal(SHARE_PAGE.WEIBO, "shareW")}>
                        <em />
                    </button>

                    {/* PC 에서 webchat qr code 노출 */}
                    {!isMobile && (
                        <button type="button" className="wechat">
                            <em />
                            <div>
                                <img src={webChatQrCode} alt="" />
                            </div>
                        </button>
                    )}

                    {/* URL 복사 */}
                    <CopyToClipboard text={shareUrl} onCopy={handleShareLink}>
                        <button type="button" className="url">
                            <em />
                        </button>
                    </CopyToClipboard>
                </div>
            </div>

            {/* 레이어 우측 상단 닫기 버튼 */}
            <ConditionWrapper
                condition={isMobile}
                wrapper={<button type="button" className="btn-ic close" onClick={close} />}
                denyWrapper={<a href={JAVASCRIPT_VOID} className="pop-close" onClick={close} />}
            >
                <span>
                    <Trans>닫기</Trans>
                </span>
            </ConditionWrapper>

            {/* 레이어 하단 닫기 버튼 */}
            {isMobile && !isWebview && (
				<div className="pop-btn" onClick={close}>
					<a href={JAVASCRIPT_VOID} className="btn">
						<strong>
							<Trans>닫기</Trans>
						</strong>
					</a>
				</div>
			)}
        </div>
    );
};

export default PlaylistShare;
